import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchRegisterData, fetchRegisterFilterOptions, fetchRegisterRecord } from '../services/externalRegisterService';
import MultiSelect from '../components/MultiSelect';
import './Suppliers.css';

/* ── Helpers ── */

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const truncate = (s, n) => {
  if (!s) return '';
  const str = String(s);
  return str.length > n ? str.slice(0, n) + '…' : str;
};

const DEFAULT_PAGE_SIZE = 50;

const isUrl = (v) => typeof v === 'string' && /^https?:\/\//.test(v.trim());

/* ── Sort Head ── */

function SortHead({ label, column, sort, onSort, colWidth, onResizeStart, onAutoFit, ...rest }) {
  const active = sort.column === column;
  const icon = active ? (sort.direction === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      className="sup-sort-head"
      style={{ position: 'relative', width: colWidth || undefined, minWidth: 60, ...(rest.style || {}) }}
      {...rest}
      onClick={undefined}
    >
      <span onClick={() => onSort(column)} style={{ cursor: 'pointer', display: 'block', paddingRight: 8 }}>
        {label}
        {active && <span className="sup-sort-ico">{icon}</span>}
      </span>
      {onResizeStart && (
        <span
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onResizeStart(e, column); }}
          onDoubleClick={(e) => { e.stopPropagation(); if (onAutoFit) onAutoFit(column); }}
          style={{ position: 'absolute', top: 0, right: 0, width: 6, height: '100%', cursor: 'col-resize', userSelect: 'none', zIndex: 1 }}
        />
      )}
    </th>
  );
}

/* ── CSV download ── */

function csvCell(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, columns, rows) {
  const header = columns.map(c => c[0]).join(',');
  const body = rows.map(r => columns.map(c => csvCell(r[c[1]])).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

/* ── Detail Modal ── */

function SectionHeading({ title }) {
  return (
    <div className="col-12" style={{ borderTop: '1px solid #dee2e6', paddingTop: 12, marginTop: 4 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</div>
    </div>
  );
}

function DetailField({ label, value, wide }) {
  const empty = value === null || value === undefined || String(value).trim() === '';
  const display = empty ? '—' : String(value);
  const isLink = !empty && isUrl(display);

  return (
    <div className={wide ? 'col-12' : 'col-12 col-md-6'}>
      <div className="sup-field-label">{label}</div>
      {isLink ? (
        <div style={{ padding: '7px 10px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6', fontSize: 13 }}>
          <a href={display} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)', wordBreak: 'break-all' }}>{display}</a>
        </div>
      ) : (
        <input
          className="sup-input"
          value={display}
          disabled
          style={{ color: empty ? 'var(--t3)' : undefined, background: '#f8f9fa' }}
        />
      )}
    </div>
  );
}

function DetailModal({ record, title, sections, onClose }) {
  if (!record) return null;
  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" style={{ maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head" style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderRadius: '14px 14px 0 0' }}>
          <h5 className="sup-modal-title mb-0">
            <span className="sup-title-main">{title}</span>
          </h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="sup-modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <div className="row g-3">
            {sections.map((sec, si) => (
              <div key={si} className="col-12">
                <SectionHeading title={sec.title} />
                <div className="row g-3">
                  {sec.fields.map((f, fi) => (
                    <DetailField
                      key={fi}
                      label={f.label}
                      value={record[f.key]}
                      wide={!!f.wide}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sup-modal-foot" style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fff', borderRadius: '0 0 14px 14px' }}>
          <button type="button" className="sup-btn" onClick={onClose}>
            <i className="bi bi-x-circle me-1"></i> Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ── */

export default function ExternalRegisterPage({
  source,
  title,
  breadcrumb,
  sourceUrl,
  columns,
  dateColumns,
  defaultFilterKeys,
  filterConfigs,
  prefsKey,
  detailTitle,
  detailSections,
}) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState({ column: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const [filterOpts, setFilterOpts] = useState({});
  const [filterOptsLoading, setFilterOptsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showFilterSettings, setShowFilterSettings] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const [lastSynced, setLastSynced] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const resizeRef = useRef({ key: null, startX: 0, startWidth: 0 });
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const downloadRef = useRef(null);
  const gearRef = useRef(null);
  const debounceTimer = useRef(null);
  const location = useLocation();

  const AVAILABLE_FILTERS = useMemo(() => filterConfigs, [filterConfigs]);

  const [visibleFilters, setVisibleFilters] = useState(() => defaultFilterKeys);
  const [pendingFilters, setPendingFilters] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // User ID for prefs
  useEffect(() => {
    if (!localStorage.getItem('focusone_uid')) {
      localStorage.setItem('focusone_uid', 'u_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
    }
    setCurrentUserId(localStorage.getItem('focusone_uid'));
  }, []);

  // Load filter prefs
  useEffect(() => {
    if (!currentUserId) return;
    fetch(`${import.meta.env.VITE_API_URL}/api/preferences/${encodeURIComponent(currentUserId)}/${prefsKey}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data?.preference_value) {
          const saved = JSON.parse(data.preference_value);
          if (Array.isArray(saved) && saved.length > 0) { setVisibleFilters(saved); return; }
        }
        setVisibleFilters(defaultFilterKeys);
      })
      .catch(() => setVisibleFilters(defaultFilterKeys));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, currentUserId]);

  const saveFilterPreferences = async () => {
    if (!pendingFilters || !currentUserId) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: currentUserId, key: prefsKey, value: JSON.stringify(pendingFilters) }),
      });
      if (!response.ok) throw new Error();
      setVisibleFilters(pendingFilters);
      setPendingFilters(null);
      setShowFilterSettings(false);
    } catch {
      setToast({ type: 'error', msg: 'Failed to save filter preferences.' });
    }
  };

  // Load filter options
  useEffect(() => {
    setFilterOptsLoading(true);
    fetchRegisterFilterOptions(source)
      .then(d => { setFilterOpts(d); setFilterOptsLoading(false); })
      .catch(() => { setFilterOpts({}); setFilterOptsLoading(false); });
  }, [source]);

  // Debounce text search filters (300ms)
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [filters]);

  // Load data
  const load = useCallback(async () => {
    setErr('');
    try {
      const params = { page, limit: pageSize };
      if (sort.column) { params.sort = sort.column; params.order = sort.direction; }
      for (const fc of filterConfigs) {
        const v = debouncedFilters[fc.key];
        if (fc.type === 'search' && typeof v === 'string' && v.trim()) {
          params.search = v.trim();
        } else if (Array.isArray(v) && v.length > 0) {
          params[fc.column] = v.join(',');
        } else if (typeof v === 'string' && v.trim()) {
          params[fc.column] = v.trim();
        }
      }
      const data = await fetchRegisterData(source, params);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      if (data.rows?.length > 0 && data.rows[0].loaded_at) {
        setLastSynced(data.rows[0].loaded_at);
      }
    } catch {
      setErr('Failed to load data.');
    }
  }, [source, page, pageSize, sort, debouncedFilters, filterConfigs]);

  useEffect(() => { load(); }, [load]);

  // Reset page and selection on filter/sort change
  useEffect(() => { setPage(1); setSelectedRows(new Set()); }, [debouncedFilters, sort]);
  // Reset selection on page change
  useEffect(() => { setSelectedRows(new Set()); }, [page]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  // Close dropdowns
  useEffect(() => {
    const handler = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) setShowDownloadMenu(false);
      if (gearRef.current && !gearRef.current.contains(e.target)) { setShowFilterSettings(false); setPendingFilters(null); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Dual scrollbar sync
  const syncFromTop = useCallback(() => {
    if (tableScrollRef.current && topScrollRef.current) tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  }, []);
  const syncFromTable = useCallback(() => {
    if (tableScrollRef.current && topScrollRef.current) topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
  }, []);

  // Column resize
  const onResizing = useCallback((e) => {
    const { key, startX, startWidth } = resizeRef.current;
    if (!key) return;
    setColumnWidths(w => ({ ...w, [key]: Math.max(60, startWidth + (e.clientX - startX)) }));
  }, []);
  const stopResize = useCallback(() => {
    resizeRef.current = { key: null, startX: 0, startWidth: 0 };
    window.removeEventListener('mousemove', onResizing);
    window.removeEventListener('mouseup', stopResize);
  }, [onResizing]);
  const startResize = useCallback((e, key) => {
    const th = e.currentTarget.parentElement;
    resizeRef.current = { key, startX: e.clientX, startWidth: th.getBoundingClientRect().width };
    window.addEventListener('mousemove', onResizing);
    window.addEventListener('mouseup', stopResize);
  }, [onResizing, stopResize]);
  const autoFitColumn = useCallback((key) => {
    if (!tableScrollRef.current) return;
    const table = tableScrollRef.current.querySelector('table');
    if (!table) return;
    const headers = table.querySelectorAll('th');
    let colIdx = -1;
    headers.forEach((th, i) => { if (th.dataset.col === key) colIdx = i; });
    if (colIdx < 0) return;
    let maxW = 60;
    table.querySelectorAll('tbody tr').forEach(tr => { const td = tr.children[colIdx]; if (td) maxW = Math.max(maxW, td.scrollWidth + 16); });
    setColumnWidths(w => ({ ...w, [key]: Math.min(maxW, 600) }));
  }, []);

  // Sync top scrollbar width
  useEffect(() => {
    if (tableScrollRef.current && topScrollRef.current?.firstChild) {
      topScrollRef.current.firstChild.style.width = tableScrollRef.current.scrollWidth + 'px';
    }
  }, [rows, pageSize]);

  const handleSort = (column) => {
    setSort(s => {
      if (s.column !== column) return { column, direction: 'asc' };
      if (s.direction === 'asc') return { column, direction: 'desc' };
      return { column: null, direction: 'asc' };
    });
  };

  const filtersActive = Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : !!v);
  const clearFilters = () => setFilters({});

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, total);

  // Row selection
  const pageRows = rows || [];
  const pageIds = useMemo(() => new Set(pageRows.map(r => r.id)), [pageRows]);
  const allPageSelected = pageRows.length > 0 && pageRows.every(r => selectedRows.has(r.id));
  const somePageSelected = pageRows.some(r => selectedRows.has(r.id));

  const toggleRow = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllPage = () => {
    if (allPageSelected) {
      setSelectedRows(prev => {
        const next = new Set(prev);
        pageRows.forEach(r => next.delete(r.id));
        return next;
      });
    } else {
      setSelectedRows(prev => {
        const next = new Set(prev);
        pageRows.forEach(r => next.add(r.id));
        return next;
      });
    }
  };

  const handleDownloadCSV = () => {
    if (!rows || rows.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    if (selectedRows.size > 0) {
      const selected = rows.filter(r => selectedRows.has(r.id));
      if (selected.length === 0) return;
      downloadCsv(`${source}_register_selected_${selected.length}_${stamp}.csv`, columns, selected);
    } else {
      downloadCsv(`${source}_register_${stamp}.csv`, columns, rows);
    }
  };

  const openDetail = async (row) => {
    setDetailLoading(true);
    try {
      const rec = await fetchRegisterRecord(source, row.id);
      setDetailRecord(rec);
    } catch {
      setToast({ type: 'error', msg: 'Failed to load record details.' });
    } finally {
      setDetailLoading(false);
    }
  };

  const csvLabel = selectedRows.size > 0
    ? `Download CSV (${selectedRows.size} selected)`
    : 'Download CSV';

  return (
    <div className="sup-page">
      <div className="mb-3">
        <h2 className="sup-heading mb-1">{title}</h2>
        <div className="sup-breadcrumb">FocusOne &middot; External Data &middot; <strong>{breadcrumb}</strong></div>
      </div>

      {err && <div className="sup-error-box mb-3">{err}</div>}

      {!rows && !err && (
        <div className="sup-table-wrap p-4">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="sup-skeleton mb-2" style={{ height: 28, width: '100%' }} />
          ))}
        </div>
      )}

      {rows && (
        <>
          <div className="sup-statsbar">
            <span className="sup-stat-gray">Total: {total.toLocaleString('en-US')} products</span>
            <span className="sup-stat-sep" />
            <span className="sup-stat-gray">Source: <a href={sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)' }}>{sourceUrl}</a></span>
            {lastSynced && (
              <>
                <span className="sup-stat-sep" />
                <span className="sup-stat-gray">Last synced: {fmtDate(lastSynced)}</span>
              </>
            )}
            {selectedRows.size > 0 && (
              <>
                <span className="sup-stat-sep" />
                <span style={{ color: 'var(--teal)', fontWeight: 600, fontSize: 12.5 }}>{selectedRows.size} selected</span>
              </>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1, maxHeight: 140, overflowY: 'auto', overflowX: 'hidden' }}>
              <div className="sup-filters-row" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
                {AVAILABLE_FILTERS.filter(f => visibleFilters.includes(f.key)).map(fc => {
                  if (fc.type === 'search' || fc.type === 'text') {
                    return (
                      <div key={fc.key} className="ms-wrap" style={{ minWidth: 180 }}>
                        <div className="ms-label">{fc.label}</div>
                        <input
                          className="sup-input"
                          placeholder={fc.placeholder || 'Search...'}
                          value={filters[fc.key] || ''}
                          onChange={(e) => {
                            let v = e.target.value;
                            if (fc.stripNonDigit) v = v.replace(/\D/g, '');
                            setFilters(f => ({ ...f, [fc.key]: v }));
                          }}
                          style={{ height: 34, fontSize: 13 }}
                        />
                      </div>
                    );
                  }
                  const opts = filterOpts[fc.column] || [];
                  return (
                    <MultiSelect
                      key={fc.key}
                      label={fc.label}
                      options={filterOptsLoading && opts.length === 0 ? [] : opts}
                      values={filters[fc.key] || []}
                      onChange={(v) => setFilters(f => ({ ...f, [fc.key]: v }))}
                      placeholder={filterOptsLoading && opts.length === 0 ? 'Loading…' : 'All'}
                      minWidth={160}
                    />
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, paddingTop: 18 }}>
              {filtersActive && (
                <button type="button" className="sup-clear-link" onClick={clearFilters}>Clear All Filters</button>
              )}
              <div ref={gearRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowFilterSettings(p => !p)}
                  className="btn btn-sm"
                  style={{ background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: 'var(--t3)' }}
                  title="Customize filters"
                >
                  <i className="bi bi-gear"></i>
                </button>
                {showFilterSettings && (
                  <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 220, zIndex: 1000, padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Customize Filters</div>
                    <div className="customize-filters-scroll" style={{ maxHeight: 280, overflowY: 'scroll', overflowX: 'hidden' }}>
                      {AVAILABLE_FILTERS.filter(f => f.type !== 'search').map(f => (
                        <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={(pendingFilters || visibleFilters).includes(f.key)}
                            onChange={() => {
                              const cur = pendingFilters || visibleFilters;
                              setPendingFilters(cur.includes(f.key) ? cur.filter(k => k !== f.key) : [...cur, f.key]);
                            }}
                            style={{ accentColor: 'var(--teal)', width: 14, height: 14 }}
                          />
                          {f.label}
                        </label>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                      <button onClick={saveFilterPreferences} disabled={!pendingFilters} style={{ background: pendingFilters ? 'var(--teal)' : 'var(--t3)', color: 'white', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 12, cursor: pendingFilters ? 'pointer' : 'not-allowed', fontWeight: 500, flex: 1 }}>
                        <i className="bi bi-check2 me-1"></i>Save
                      </button>
                      <button onClick={() => { setPendingFilters(null); setShowFilterSettings(false); }} style={{ background: 'var(--card)', color: 'var(--t3)', border: '1px solid #dee2e6', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <div ref={downloadRef} style={{ position: 'relative' }}>
                <button onClick={() => setShowDownloadMenu(p => !p)} className="btn btn-sm" style={{ background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} title="Download">
                  <i className="bi bi-download"></i>
                </button>
                {showDownloadMenu && (
                  <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 200, zIndex: 1000, overflow: 'hidden' }}>
                    <button onClick={() => { handleDownloadCSV(); setShowDownloadMenu(false); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', border: 'none', background: 'var(--card)', cursor: 'pointer', fontSize: 13, color: 'var(--t1)' }} onMouseEnter={e => e.currentTarget.style.background = '#EFF8FF'} onMouseLeave={e => e.currentTarget.style.background = 'var(--card)'}>
                      <i className="bi bi-file-earmark-spreadsheet" style={{ color: 'var(--teal)' }}></i>{csvLabel}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="sup-table-wrap">
            <style>{`
.table-scroll-container::-webkit-scrollbar { height: 8px; width: 8px; }
.table-scroll-container::-webkit-scrollbar-track { background: #f1f1f1; }
.table-scroll-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.table-scroll-container::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
.table-top-scroll::-webkit-scrollbar { height: 8px; }
.table-top-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
.table-top-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.table-top-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
.customize-filters-scroll::-webkit-scrollbar { width: 8px; }
.customize-filters-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.customize-filters-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.customize-filters-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
.customize-filters-scroll { scrollbar-width: thin; scrollbar-color: #c1c1c1 #f1f1f1; }
.sup-table td { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 0; }
.sup-table td.ext-action-cell { overflow: visible; max-width: none; }
            `}</style>
            <div ref={topScrollRef} className="table-top-scroll" onScroll={syncFromTop} style={{ overflowX: 'auto', overflowY: 'hidden', height: 12, marginBottom: 4 }}>
              <div style={{ height: 1 }} />
            </div>
            <div ref={tableScrollRef} className="table-scroll-container" onScroll={syncFromTable} style={{ overflowX: 'auto', overflowY: 'auto', width: '100%', WebkitOverflowScrolling: 'touch', maxHeight: '72vh' }}>
              <table className="sup-table" style={{ minWidth: (columns.length + 2) * 140, tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: 36, minWidth: 36, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                        onChange={toggleAllPage}
                        style={{ accentColor: 'var(--teal)', width: 15, height: 15, cursor: 'pointer' }}
                      />
                    </th>
                    <th style={{ width: 50, minWidth: 50, textAlign: 'center' }}>Open</th>
                    {columns.map(([lbl, col]) => (
                      <SortHead key={col} label={lbl} column={col} sort={sort} onSort={handleSort}
                        colWidth={columnWidths[col]} onResizeStart={startResize} onAutoFit={autoFitColumn}
                        data-col={col} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, idx) => (
                    <tr key={r.id || idx}>
                      <td className="ext-action-cell" style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedRows.has(r.id)}
                          onChange={() => toggleRow(r.id)}
                          style={{ accentColor: 'var(--teal)', width: 15, height: 15, cursor: 'pointer' }}
                        />
                      </td>
                      <td className="ext-action-cell" style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => openDetail(r)}
                          style={{
                            background: 'transparent', border: '1px solid #dee2e6', borderRadius: 6,
                            padding: '2px 6px', cursor: 'pointer', color: 'var(--teal)', fontSize: 13,
                            lineHeight: 1,
                          }}
                          title="Open record details"
                        >
                          <i className="bi bi-box-arrow-up-right" />
                        </button>
                      </td>
                      {columns.map(([, col]) => {
                        const val = r[col];
                        if (dateColumns?.has(col)) return <td key={col}>{fmtDate(val)}</td>;
                        if ((col === 'ema_url' || col === 'medicine_url') && val) return <td key={col}><a href={val} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)' }} title={val}>{truncate(val, 40)}</a></td>;
                        return <td key={col} title={val || ''}>{truncate(String(val ?? '—'), 40)}</td>;
                      })}
                    </tr>
                  ))}
                  {pageRows.length === 0 && (
                    <tr><td colSpan={columns.length + 2} style={{ textAlign: 'center', padding: 40, color: 'var(--t3)' }}>No data found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', borderTop: '1px solid #eef3f7', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flexWrap: 'nowrap' }}>
              <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
                <button type="button" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
                {(() => {
                  const win = 5;
                  let s = Math.max(1, safePage - Math.floor(win / 2));
                  let e = Math.min(pageCount, s + win - 1);
                  s = Math.max(1, e - win + 1);
                  const nums = [];
                  for (let i = s; i <= e; i++) nums.push(i);
                  return nums.map(n => (
                    <button key={n} type="button" onClick={() => setPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>
                  ));
                })()}
                <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
              </nav>
              <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
                Showing {total === 0 ? 0 : pageStart + 1}–{pageEnd} of {total.toLocaleString('en-US')} products
              </span>
            </div>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {detailRecord && detailSections && (
        <DetailModal
          record={detailRecord}
          title={detailTitle || `${title} Record Details`}
          sections={detailSections}
          onClose={() => setDetailRecord(null)}
        />
      )}

      {detailLoading && (
        <div className="sup-modal-backdrop" style={{ cursor: 'wait' }}>
          <div style={{ color: '#fff', fontSize: 16 }}>Loading…</div>
        </div>
      )}

      {toast && (
        <div className={`sup-toast${toast.type === 'error' ? ' error' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
